import { Link, useNavigate } from "react-router-dom";
import { useAuth, homeForRole } from "../lib/auth";
import { TID } from "../constants/testIds";
import { useEffect } from "react";
import {
  Cpu, GitCompare, Globe, Sparkles, Fingerprint, Lock, ArrowUpRight,
  CheckCircle2, ShieldCheck, ArrowRight,
} from "lucide-react";
import Logo from "../components/Logo";

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate(homeForRole(user.role));
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid={TID.landingHero}>
      {/* Top nav */}
      <header className="border-b border-slate-200 sticky top-0 bg-white/95 backdrop-blur z-50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo variant="dark" size={30} to="/" testid="landing-logo"/>
            <span className="hidden md:inline-block h-4 w-px bg-slate-200"/>
            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 hidden md:inline">Employee Relations</span>
          </div>
          <nav className="flex items-center gap-3">
            <Link to="/login" className="btn-ghost" data-testid={TID.landingLogin}>Sign in</Link>
            <Link to="/register" className="btn-primary" data-testid={TID.landingRegister}>
              Get started <ArrowUpRight size={12} strokeWidth={2}/>
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero , on-light */}
      <section className="relative overflow-hidden on-light" style={{
        background: "radial-gradient(ellipse 60% 50% at 100% 0%, #FDF3E4 0%, transparent 60%), radial-gradient(ellipse 40% 40% at 0% 100%, #EDECFA 0%, transparent 60%), #ffffff",
      }}>
        <div className="max-w-7xl mx-auto px-6 py-20 lg:py-28 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-slate-600 mb-6 bg-white border border-slate-200 rounded-full pl-2 pr-3 py-1">
              <span className="live-dot ai-dot" style={{ background: "#E4943A" }}></span>
              Sentinel Analytics · Dual Perspective · Enterprise Intelligence
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight text-slate-900 leading-[1.02] font-semibold">
              The nerve center for<br/>
              <span className="text-brand italic font-editorial">employee relations,</span><br/>
              re-engineered with intelligence.
            </h1>
            <p className="mt-8 text-lg text-slate-600 leading-relaxed max-w-2xl">
              <span className="text-brand font-semibold">PropHR</span> is the enterprise employee-relations platform where every complaint, grievance, and investigation is triaged, summarized, and approved through governed workflows , with human judgment always at the wheel.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/register" className="btn-primary text-base px-6 py-3">
                Start free trial <ArrowUpRight size={14} strokeWidth={2}/>
              </Link>
              <Link to="/login" className="btn-accent text-base px-6 py-3">
                Sign in <ArrowRight size={14} strokeWidth={2}/>
              </Link>
            </div>

            <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6 text-slate-500 border-t border-slate-200 pt-8">
              {[
                { value: "2,847",  label: "cases triaged / day" },
                { value: "96%",    label: "Sentinel classification accuracy" },
                { value: "-58%",   label: "time to resolution" },
                { value: "SOC 2",  label: "Type II certified" },
              ].map((s) => (
                <div key={s.label}>
                  <div className="font-display text-3xl font-semibold text-brand">{s.value}</div>
                  <div className="text-xs mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* AI intake card */}
          <div className="lg:col-span-5">
            <div className="ai-surface tracing-beam p-6 relative on-dark rounded">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Cpu size={16} strokeWidth={1.75} style={{ color: "#E4943A" }}/>
                  <span className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#E4943A" }}>Sentinel Analytics · Case Intake</span>
                </div>
                <span className="text-[10px] font-mono text-[color:var(--fg-on-dark-muted)]">CASE #PHR-2026-0142</span>
              </div>
              <div className="text-white font-editorial text-xl mb-4 leading-snug">
                &ldquo;I&apos;ve raised the shift-rotation issue three times to my manager. Nothing has changed and I feel targeted.&rdquo;
              </div>
              <div className="border-t border-[color:var(--sidebar-border)] pt-4 space-y-2 font-mono text-[11px] text-[color:var(--fg-on-dark-muted)]">
                <div><span className="text-[color:var(--fg-on-dark-muted)]">CLASSIFY ›</span> <span className="text-white">Interpersonal conflict + policy violation</span></div>
                <div><span className="text-[color:var(--fg-on-dark-muted)]">RISK ›</span> <span style={{ color: "#E4943A" }}>Medium (58)</span> · retaliation vector detected</div>
                <div><span className="text-[color:var(--fg-on-dark-muted)]">POLICY ›</span> <span className="text-white">§4.2 Fair scheduling, §7.1 Grievance</span></div>
                <div><span className="text-[color:var(--fg-on-dark-muted)]">SIMILAR ›</span> <span className="text-white">3 priors matched · avg TTR 4.2d</span></div>
                <div><span className="text-[color:var(--fg-on-dark-muted)]">ACTION ›</span> <span className="text-white">Route to HR · SLA 48h · Notify manager</span></div>
              </div>
              <div className="mt-5 flex items-center justify-between text-[10px] font-mono text-[color:var(--fg-on-dark-muted)]">
                <span>SENTINEL ANALYTICS · confidence 0.87</span>
                <span className="live-dot ai-dot" style={{ background: "#E4943A" }}></span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-4 on-light">
              {[
                { Icon: Fingerprint, title: "Fingerprinted", body: "Every upload SHA-256 verified." },
                { Icon: CheckCircle2, title: "Approval Workflow", body: "Nothing published without sign-off." },
                { Icon: Globe, title: "Multi-tenant", body: "Isolated data per company." },
                { Icon: Lock, title: "Compliant", body: "GDPR · CCPA · EEOC · SOC 2." },
              ].map((c) => (
                <div key={c.title} className="data-cell p-4">
                  <c.Icon size={18} className="text-brand mb-2" strokeWidth={1.75}/>
                  <div className="text-sm font-semibold text-slate-900">{c.title}</div>
                  <div className="text-xs text-slate-500">{c.body}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Feature strip , light */}
      <section className="border-t border-slate-200 py-20 bg-slate-50/60 on-light">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-[10px] font-mono uppercase tracking-widest text-brand mb-4">Sentinel Analytics · Capabilities</div>
          <h2 className="font-display text-4xl lg:text-5xl text-slate-900 tracking-tight max-w-3xl font-semibold">
            Three intelligent agents, one governed workflow.
          </h2>
          <div className="mt-10 grid md:grid-cols-3 gap-4">
            {[
              { name: "Dual Perspective", desc: "Sentinel Analytics argues reporter vs subject perspectives, then synthesizes a neutral, evidence-based recommendation.", icon: GitCompare, tint: "bg-brand" },
              { name: "Enterprise Intelligence", desc: "Board-level org intelligence: hotspots, systemic risks, and top strategic recommendations across your workspace.", icon: Globe, tint: "bg-brand-accent" },
              { name: "Sentinel Synthesis", desc: "Hyper-intelligent case synthesis linking non-obvious patterns across incidents, policies, and departments.", icon: Sparkles, tint: "bg-brand" },
            ].map((f) => (
              <div key={f.name} className="data-cell p-6 h-full">
                <div className={"w-10 h-10 flex items-center justify-center mb-4 rounded " + f.tint}>
                  <f.icon size={18} strokeWidth={1.75} className="text-white"/>
                </div>
                <div className="font-display text-lg font-semibold text-slate-900">{f.name}</div>
                <div className="text-sm text-slate-600 mt-2 leading-relaxed">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow strip , light */}
      <section className="border-t border-slate-200 py-20 on-light">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-[10px] font-mono uppercase tracking-widest text-brand mb-4">Governance-first</div>
          <h2 className="font-display text-4xl text-slate-900 tracking-tight max-w-3xl font-semibold">
            Every sensitive step is channeled through approval.
          </h2>
          <div className="mt-10 grid md:grid-cols-4 gap-6">
            {[
              { step: "01", title: "Report", desc: "Employee submits case (anonymously if preferred). Sentinel Analytics classifies & risk-scores.", color: "#373281" },
              { step: "02", title: "Investigate", desc: "HR gathers evidence. Every file fingerprinted with SHA-256 + upload reason.", color: "#373281" },
              { step: "03", title: "Findings & Corrective Actions", desc: "HR drafts findings. Company Admin approves before it goes final.", color: "#E4943A" },
              { step: "04", title: "Archive", desc: "Evidence is never deleted. Archival requires approval; retention policy applied.", color: "#E4943A" },
            ].map((s) => (
              <div key={s.step} className="pt-4 border-t-2" style={{ borderColor: s.color }}>
                <div className="font-mono text-xs mb-2" style={{ color: s.color }}>{s.step}</div>
                <div className="font-display text-lg font-semibold text-slate-900">{s.title}</div>
                <div className="text-sm text-slate-600 mt-1 leading-relaxed">{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA , dark */}
      <section className="relative overflow-hidden on-dark" style={{ background: "linear-gradient(135deg, #1A1743 0%, #373281 60%, #221D53 100%)" }}>
        <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full opacity-30 pointer-events-none blur-3xl"
             style={{ background: "radial-gradient(circle, #E4943A 0%, transparent 70%)" }}/>
        <div className="relative max-w-7xl mx-auto px-6 py-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <Logo variant="light" size={40} className="mb-6"/>
            <h3 className="font-display text-4xl text-white tracking-tight font-semibold">Ready to modernize employee relations?</h3>
            <p className="text-[color:var(--fg-on-dark-muted)] mt-3 max-w-xl">
              Deploy PropHR to your entire organization in under 60 minutes. Try demo tenant <span className="font-mono text-white bg-white/10 px-1.5 py-0.5 rounded">acme</span> to explore.
            </p>
          </div>
          <div className="flex gap-3">
            <Link to="/register" className="btn-accent px-6 py-3">Start free trial <ArrowRight size={14}/></Link>
            <Link to="/login" className="px-6 py-3 border border-white/40 text-white hover:border-white transition-colors rounded text-sm font-semibold">Book a demo</Link>
          </div>
        </div>
      </section>

      <footer className="enterprise-footer py-6 text-center text-[11px] font-mono uppercase tracking-widest">
        <div className="flex items-center justify-center gap-2">
          <Logo variant="light" size={18}/>
          <span>© 2026 · Confidential Employee Relations Platform · SOC 2 · GDPR · CCPA · EEOC · ISO 27001</span>
        </div>
      </footer>
    </div>
  );
}
